import React from "react";
import {useTranslation} from "react-i18next";

import useDialog from "../../../components/useDialog";

import MDButton from "materialDashboard/components/MDButton";
import MDBox from "materialDashboard/components/MDBox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import {
    useCreateIncidentExportTask,
    useCreatePeopleCaughtExportTask,
    useIncidentExportMutation,
    usePeopleCaughtExportMutation
} from "../query";
import MDLoadingButton from "../../../components/MDLoadingButton";
import WorkgroupAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete/WorkgroupAutocomplete";
import {Alert} from "@mui/material";
import ExportTaskDialogContent from "../../../components/ExportTaskDialogContent";


const useExportState = ({close, incidentsFilters}) => {
    const [format, setFormat] = React.useState("INCIDENT_CSV");

    const incidentSyncExportMutation = useIncidentExportMutation();
    const peopleCaughtSyncExportMutation = usePeopleCaughtExportMutation();

    const incidentAsyncExportMutation = useCreateIncidentExportTask();
    const peopleCaughtAsyncExportMutation = useCreatePeopleCaughtExportTask();

    const [exportTaskId, setExportTaskId] = React.useState(null);
    const [workgroupId, setWorkgroupId] = React.useState(null);
    const [asyncExport, setAsyncExport] = React.useState(false);

    const handleClose = () => {
        close();
        setExportTaskId(null);
    }

    const handleExport = async () => {
        if (asyncExport) {
            let data;
            if (format === "INCIDENT_CSV") {
                data = await incidentAsyncExportMutation.mutateAsync({
                    workgroupId: workgroupId,
                    filters: incidentsFilters,
                    data: {format: "CSV"}
                });
            } else if (format === "PEOPLE_CAUGHT_CSV") {
                data = await peopleCaughtAsyncExportMutation.mutateAsync({
                    workgroupId: workgroupId,
                    filters: incidentsFilters,
                    data: {format: "CSV"}
                });
            }
            setExportTaskId(data.id);
        } else {
            if (format === "INCIDENT_CSV") {
                await incidentSyncExportMutation.mutateAsync({...incidentsFilters, format: "CSV"});
            } else if (format === "PEOPLE_CAUGHT_CSV") {
                await peopleCaughtSyncExportMutation.mutateAsync({...incidentsFilters, format: "CSV"});
            }
            close();
        }
    }

    const handleChangeFormat = (event) => {
        setFormat(event.target.value);
    };

    const handleChangeAsyncExport = (event) => {
        setAsyncExport(event.target.value === 'true');
    }

    const handleChangeWorkgroupId = (newValue) => {
        setWorkgroupId(newValue);
    }

    return {
        format,
        handleChangeFormat,
        handleExport,
        asyncExport,
        handleChangeAsyncExport,
        isLoading: incidentSyncExportMutation.isLoading ||
            peopleCaughtSyncExportMutation.isLoading ||
            incidentAsyncExportMutation.isLoading ||
            peopleCaughtAsyncExportMutation.isLoading,
        handleChangeWorkgroupId,
        workgroupId,
        exportTaskId,
        handleClose
    }
}


const ExportDialog = ({isOpen, close, incidentsFilters = {}}) => {

    const exportState = useExportState({close, incidentsFilters});

    return <Dialog onClose={exportState.handleClose} open={isOpen} maxWidth="lg" fullWidth>
        {
            exportState.exportTaskId === null ?
                <ExportIncidentDialogContent exportState={exportState}/>
                :
                <ExportTaskDialogContent exportTaskId={exportState.exportTaskId} handleClose={exportState.handleClose}/>
        }
    </Dialog>
}


const ExportIncidentDialogContent = ({exportState}) => {
    const {t} = useTranslation(["incidents", "validation"]);
    return <>
        <DialogTitle>{t('export.title')}</DialogTitle>
        <DialogContent>
            {t('export.description')}

            <FormControl fullWidth>
                <RadioGroup
                    name="format"
                    value={exportState.format}
                    onChange={exportState.handleChangeFormat}
                >
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <FormControlLabel value="INCIDENT_CSV" control={<Radio/>} label={<MDBox>
                                    <MDTypography variant="body2">
                                        {t('export.incidentsCSV')}
                                    </MDTypography>
                                </MDBox>}/>
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <FormControlLabel value="PEOPLE_CAUGHT_CSV" control={<Radio/>} label={<MDBox>
                                    <MDTypography variant="body2">
                                        {t('export.peopleCaughtCSV')}
                                    </MDTypography>
                                </MDBox>}/>
                            </div>
                        </Grid>
                    </Grid>
                </RadioGroup>
            </FormControl>
            <FormControl fullWidth>
                {t('export.exportMode')}
                <RadioGroup
                    name="asyncExport"
                    value={exportState.asyncExport}
                    onChange={exportState.handleChangeAsyncExport}>
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Radio/>}
                                label={t('export.syncExportMode')}
                                value={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={<Radio/>}
                                label={t('export.asyncExportMode')}
                                value={true}
                            />
                        </Grid>
                    </Grid>
                </RadioGroup>
            </FormControl>
            {
                exportState.asyncExport && <WorkgroupAutocomplete value={exportState.workgroupId}
                                                                  onChange={exportState.handleChangeWorkgroupId}
                                                                  MDInputProps={{
                                                                      label: t('export.workgroupLabel'),
                                                                      error: !Boolean(exportState.workgroupId),
                                                                      helperText: Boolean(exportState.workgroupId) ? "" : t('validation:required')
                                                                  }}
                                                                  sx={{mt: 3}}/>
            }
            <Alert severity="info" sx={{mt: 3}}>
                {
                    exportState.asyncExport && t('export.asyncExportModeDescription')
                }
                {
                    !exportState.asyncExport && t('export.syncExportModeDescription')
                }

            </Alert>
        </DialogContent>
        <DialogActions>
            <MDButton onClick={exportState.handleClose} color="info" variant="outlined">
                {t('export.cancel')}
            </MDButton>
            <MDLoadingButton onClick={exportState.handleExport}
                             color="info"
                             variant="gradient"
                             disabled={exportState.asyncExport && !exportState.workgroupId}
                             loading={exportState.isLoading}>
                {t('export.buttonLabel')}
            </MDLoadingButton>
        </DialogActions>
    </>
}


const ExportToolbar = ({incidentsFilters}) => {
    const {t} = useTranslation(["incidents"]);
    const dialogState = useDialog();

    return <MDBox py={2} px={3}>
        <MDLoadingButton variant="outlined"
                         color="info"
                         onClick={dialogState.open}
                         sx={{alignSelf: "flex-start"}}>
            {t('export.buttonLabel')}
        </MDLoadingButton>
        <ExportDialog isOpen={dialogState.isOpen}
                      close={dialogState.close}
                      incidentsFilters={incidentsFilters}/>
    </MDBox>
}

export default ExportToolbar;